import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Field,
  Flex,
  Heading,
  Input,
  Stack,
  Table,
  Tabs,
  Text
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../api.js';
import { useAuth } from '../context/AuthContext';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const emptyTransferRow = () => ({
  transferNo: '',
  principal: '',
  interestRate: '',
  date: new Date().toISOString().split('T')[0],
  splitParts: null
});

const getArticleRemaining = (article) =>
  Number(article?.remainingPrincipal ?? article?.totalPrincipal ?? article?.amount ?? 0);

const buildSplitParts = (baseNo, totalPrincipal, splitAmount) => {
  const base = (baseNo || 'ITEM').trim();
  const total = Number(totalPrincipal);
  const split = Number(splitAmount);
  if (!base || !(split > 0) || !(total > split)) return null;
  const remainder = Math.round((total - split) * 100) / 100;
  return [
    { id: '1', transferNo: `${base}/1`, principal: split, selected: true },
    { id: '2', transferNo: `${base}/2`, principal: remainder, selected: false }
  ];
};

// --- Vadla Users Tab ---
const VadlaUsersTab = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', mobile: '', no: '' });
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    const res = await apiGet('/vadla/users');
    setUsers(res.users || []);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', mobile: '', no: '' });
    setOpen(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({ name: user.name || '', mobile: user.mobile || '', no: user.no || '' });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      alert('Name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        mobile: form.mobile.trim() || null,
        no: form.no.trim() || null
      };
      if (editing) {
        await apiPut(`/vadla/users/${editing._id}`, payload);
      } else {
        await apiPost('/vadla/users', payload);
      }
      setOpen(false);
      fetchUsers();
    } catch (err) {
      alert(err.message || 'Failed to save vadla user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vadla ledger?')) return;
    try {
      await apiDelete(`/vadla/users/${id}`);
      fetchUsers();
    } catch (err) {
      alert(err.message || 'Failed to delete');
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">Create Vadla User</Heading>
        <Button colorPalette="blue" onClick={openCreate}>Add Vadla Ledger</Button>
      </Flex>

      <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Mobile</Table.ColumnHeader>
              <Table.ColumnHeader>No</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {users.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={4}>
                  <Text color="gray.500">No vadla ledgers yet.</Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              users.map((user) => (
                <Table.Row key={user._id}>
                  <Table.Cell>{user.name}</Table.Cell>
                  <Table.Cell>{user.mobile}</Table.Cell>
                  <Table.Cell>{user.no || '-'}</Table.Cell>
                  <Table.Cell textAlign="right">
                    <Button size="sm" mr={2} onClick={() => openEdit(user)}>Edit</Button>
                    <Button size="sm" colorPalette="red" onClick={() => handleDelete(user._id)}>Delete</Button>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      <Dialog.Root open={open} onOpenChange={(e) => !e.open && setOpen(false)}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{editing ? 'Edit Vadla Ledger' : 'Create Vadla Ledger'}</Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              <Stack gap={3}>
                <Field.Root>
                  <Field.Label>Name</Field.Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </Field.Root>
                <Field.Root>
                  <Field.Label>Mobile No (optional)</Field.Label>
                  <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
                </Field.Root>
                <Field.Root>
                  <Field.Label>No (optional)</Field.Label>
                  <Input value={form.no} onChange={(e) => setForm({ ...form, no: e.target.value })} />
                </Field.Root>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button colorPalette="blue" onClick={handleSave} loading={saving}>Save</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
};

// --- Vadla Entry Tab ---
const VadlaEntryTab = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.permissions?.articles?.edit;
  const canDelete = user?.role === 'admin' || user?.permissions?.articles?.delete;

  const [vadlaUsers, setVadlaUsers] = useState([]);
  const [selectedVadlaId, setSelectedVadlaId] = useState('');
  const [articles, setArticles] = useState([]);
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForms, setTransferForms] = useState({});
  const [saving, setSaving] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releasingEntry, setReleasingEntry] = useState(null);
  const [releaseDate, setReleaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [releasePreview, setReleasePreview] = useState(null);
  const [editEntryOpen, setEditEntryOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editEntryForm, setEditEntryForm] = useState(emptyTransferRow());
  const [viewEntry, setViewEntry] = useState(null);
  const [splitDialogArticleId, setSplitDialogArticleId] = useState(null);
  const [splitNoInput, setSplitNoInput] = useState('');
  const [splitAmountInput, setSplitAmountInput] = useState('');

  const fetchBase = async () => {
    const [usersRes, articlesRes, entriesRes] = await Promise.all([
      apiGet('/vadla/users'),
      apiGet('/vadla/articles/available'),
      apiGet('/vadla/entries')
    ]);
    setVadlaUsers(usersRes.users || []);
    setArticles(articlesRes.articles || []);
    setEntries(entriesRes.entries || []);
  };

  useEffect(() => {
    fetchBase();
  }, []);

  const filteredArticles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter((a) => {
      const nameOrNo = (a.nameOrNo || '').toLowerCase();
      const clientName = (a.client?.name || a.clientId?.name || '').toLowerCase();
      return nameOrNo.includes(q) || clientName.includes(q);
    });
  }, [articles, search]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openTransfer = () => {
    if (!selectedVadlaId) {
      alert('Please select vadla ledger first');
      return;
    }
    if (selectedIds.length === 0) {
      alert('Please select at least one item');
      return;
    }
    const forms = {};
    selectedIds.forEach((id) => {
      const article = articles.find((a) => a._id === id);
      const remaining = getArticleRemaining(article);
      forms[id] = {
        ...emptyTransferRow(),
        transferNo: '',
        principal: String(remaining),
        interestRate: article?.interestRate ?? '',
        splitParts: null
      };
    });
    setTransferForms(forms);
    setTransferOpen(true);
  };

  const updateTransferForm = (articleId, field, value) => {
    setTransferForms((prev) => ({
      ...prev,
      [articleId]: { ...prev[articleId], [field]: value }
    }));
  };

  const openSplitDialog = (articleId) => {
    setSplitDialogArticleId(articleId);
    setSplitNoInput('');
    setSplitAmountInput('');
  };

  const closeSplitDialog = () => {
    setSplitDialogArticleId(null);
    setSplitNoInput('');
    setSplitAmountInput('');
  };

  const applySplit = () => {
    if (!splitDialogArticleId) return;
    const no = splitNoInput.trim();
    if (!no) {
      alert('Please enter No');
      return;
    }
    const article = articles.find((a) => a._id === splitDialogArticleId);
    const row = transferForms[splitDialogArticleId] || emptyTransferRow();
    const remaining = getArticleRemaining(article);
    const parts = buildSplitParts(no, remaining, splitAmountInput);
    if (!parts) {
      alert(`Enter a valid split amount between 1 and ${remaining - 0.01}`);
      return;
    }
    setTransferForms((prev) => ({
      ...prev,
      [splitDialogArticleId]: {
        ...row,
        transferNo: no,
        splitParts: parts,
        principal: ''
      }
    }));
    closeSplitDialog();
  };

  const clearSplit = (articleId) => {
    const article = articles.find((a) => a._id === articleId);
    const remaining = getArticleRemaining(article);
    setTransferForms((prev) => ({
      ...prev,
      [articleId]: {
        ...(prev[articleId] || emptyTransferRow()),
        splitParts: null,
        principal: String(remaining)
      }
    }));
  };

  const toggleSplitPart = (articleId, partId) => {
    setTransferForms((prev) => {
      const row = prev[articleId];
      if (!row?.splitParts) return prev;
      return {
        ...prev,
        [articleId]: {
          ...row,
          splitParts: row.splitParts.map((p) =>
            p.id === partId ? { ...p, selected: !p.selected } : p
          )
        }
      };
    });
  };

  const handleTransferSubmit = async () => {
    const items = [];
    for (const articleId of selectedIds) {
      const row = transferForms[articleId] || emptyTransferRow();
      if (row.splitParts?.length) {
        const selected = row.splitParts.filter((p) => p.selected);
        if (selected.length === 0) {
          alert('Select at least one split part to transfer');
          return;
        }
        selected.forEach((part) => {
          items.push({
            articleId,
            transferNo: part.transferNo,
            no: part.transferNo,
            principal: Number(part.principal),
            interestRate: Number(row.interestRate),
            date: row.date
          });
        });
      } else {
        if (!row.principal || Number(row.principal) <= 0) {
          alert('Enter valid principal for each item');
          return;
        }
        items.push({
          articleId,
          transferNo: row.transferNo,
          no: row.transferNo,
          principal: Number(row.principal),
          interestRate: Number(row.interestRate),
          date: row.date
        });
      }
    }

    setSaving(true);
    try {
      await apiPost('/vadla/entries', { vadlaUserId: selectedVadlaId, items });
      alert(`Transferred ${items.length} vadla entry/entries successfully`);
      setTransferOpen(false);
      setSelectedIds([]);
      fetchBase();
    } catch (err) {
      alert(err.message || 'Transfer failed');
    } finally {
      setSaving(false);
    }
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setEditEntryForm({
      transferNo: entry.transferNo || '',
      principal: String(entry.principal ?? ''),
      interestRate: String(entry.interestRate ?? ''),
      date: entry.transferDate
        ? new Date(entry.transferDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      releaseDate: entry.releaseDate
        ? new Date(entry.releaseDate).toISOString().split('T')[0]
        : ''
    });
    setEditEntryOpen(true);
  };

  const handleEditEntrySubmit = async () => {
    if (!editingEntry) return;
    if (!editEntryForm.principal || Number(editEntryForm.principal) <= 0) {
      alert('Please enter valid principal');
      return;
    }
    setSaving(true);
    try {
      await apiPut(`/vadla/entries/${editingEntry._id}`, {
        transferNo: editEntryForm.transferNo,
        principal: Number(editEntryForm.principal),
        interestRate: Number(editEntryForm.interestRate),
        date: editEntryForm.date,
        ...(editingEntry.released ? { releaseDate: editEntryForm.releaseDate } : {})
      });
      setEditEntryOpen(false);
      setEditingEntry(null);
      fetchBase();
    } catch (err) {
      alert(err.message || 'Failed to update vadla entry');
    } finally {
      setSaving(false);
    }
  };

  const loadReleasePreview = async (entryId, date) => {
    try {
      const params = new URLSearchParams();
      if (date) params.set('releaseDate', date);
      const preview = await apiGet(`/vadla/entries/${entryId}/release-preview?${params.toString()}`);
      setReleasePreview(preview);
    } catch (err) {
      setReleasePreview(null);
    }
  };

  const openRelease = (entry) => {
    const today = new Date().toISOString().split('T')[0];
    setReleasingEntry(entry);
    setReleaseDate(today);
    setReleasePreview(null);
    setReleaseOpen(true);
    loadReleasePreview(entry._id, today);
  };

  const handleReleaseDateChange = (date) => {
    setReleaseDate(date);
    if (releasingEntry) loadReleasePreview(releasingEntry._id, date);
  };

  const handleReleaseSubmit = async () => {
    if (!releasingEntry || !releaseDate) return;
    setSaving(true);
    try {
      await apiPost(`/vadla/entries/${releasingEntry._id}/release`, { releaseDate });
      alert('Vadla entry released successfully');
      setReleaseOpen(false);
      setReleasingEntry(null);
      fetchBase();
    } catch (err) {
      alert(err.message || 'Release failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entryId, released = false) => {
    const msg = released
      ? 'Delete this released vadla entry?'
      : 'Delete this vadla entry? The item will be available for transfer again.';
    if (!window.confirm(msg)) return;
    try {
      await apiDelete(`/vadla/entries/${entryId}`);
      fetchBase();
    } catch (err) {
      alert(err.message || 'Failed to delete');
    }
  };

  const filteredByVadla = selectedVadlaId
    ? entries.filter((e) => e.vadlaUserId?._id === selectedVadlaId || e.vadlaUserId === selectedVadlaId)
    : entries;
  const selectedVadlaEntries = filteredByVadla.filter((e) => !e.released);
  const releasedVadlaEntries = filteredByVadla.filter((e) => e.released);

  return (
    <Box>
      <Heading size="md" mb={4}>Vadla Entry</Heading>

      <Box mb={4} p={4} borderWidth="1px" borderRadius="lg">
        <Text fontSize="sm" fontWeight="bold" mb={2}>Select Vadla Ledger</Text>
        <select
          value={selectedVadlaId}
          onChange={(e) => setSelectedVadlaId(e.target.value)}
          style={{ width: '100%', maxWidth: '400px', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
        >
          <option value="">-- Select Vadla Ledger --</option>
          {vadlaUsers.map((u) => (
            <option key={u._id} value={u._id}>
              {u.name}{u.mobile ? ` (${u.mobile})` : ''}{u.no ? ` - ${u.no}` : ''}
            </option>
          ))}
        </select>
      </Box>

      <Box mb={4} p={4} borderWidth="1px" borderRadius="lg">
        <Flex justify="space-between" align="center" mb={3} gap={2} wrap="wrap">
          <Text fontWeight="bold">Select Items to Transfer</Text>
          <Button colorPalette="teal" onClick={openTransfer} isDisabled={!selectedVadlaId || selectedIds.length === 0}>
            Transfer ({selectedIds.length})
          </Button>
        </Flex>
        <Input
          mb={3}
          placeholder="Search by item name/no or client"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Box maxH="280px" overflowY="auto">
          {filteredArticles.length === 0 ? (
            <Text color="gray.500" fontSize="sm">No available items.</Text>
          ) : (
            <Stack gap={2}>
              {filteredArticles.map((article) => (
                <Flex
                  key={article._id}
                  p={2}
                  borderWidth="1px"
                  borderRadius="md"
                  align="center"
                  gap={3}
                  bg={selectedIds.includes(article._id) ? 'blue.50' : 'white'}
                >
                  <Checkbox.Root
                    checked={selectedIds.includes(article._id)}
                    onCheckedChange={() => toggleSelect(article._id)}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
                  </Checkbox.Root>
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="medium">
                      {article.nameOrNo || 'N/A'} — {article.client?.name || article.clientId?.name || 'Client'}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Available: ₹{article.remainingPrincipal ?? article.totalPrincipal ?? article.amount ?? 0}
                      {article.vadlaPrincipal > 0 ? ` (on vadla: ₹${article.vadlaPrincipal})` : ''}
                      {' '}| Date: {formatDate(article.date)}
                    </Text>
                  </Box>
                </Flex>
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      <Box p={4} borderWidth="1px" borderRadius="lg">
        <Text fontWeight="bold" mb={3}>Active Vadla Links</Text>
        {selectedVadlaEntries.length === 0 ? (
          <Text color="gray.500" fontSize="sm">No active vadla entries.</Text>
        ) : (
          <Box overflowX="auto">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Vadla</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Item</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>No</th>
                  <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Principal</th>
                  <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Rate</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                  <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {selectedVadlaEntries.map((entry) => (
                  <tr key={entry._id}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{entry.vadlaUser?.name}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>
                      {entry.article?.nameOrNo || 'N/A'}
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{entry.transferNo || '-'}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>₹{entry.principal}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>{entry.interestRate}%</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(entry.transferDate)}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                      <Button size="xs" variant="outline" mr={2} onClick={() => setViewEntry(entry)}>View</Button>
                      {canEdit && (
                        <>
                          <Button size="xs" variant="outline" mr={2} onClick={() => openEditEntry(entry)}>Edit</Button>
                          <Button size="xs" colorPalette="green" mr={2} onClick={() => openRelease(entry)}>Release</Button>
                        </>
                      )}
                      {canDelete && (
                        <Button size="xs" colorPalette="red" variant="outline" onClick={() => handleDeleteEntry(entry._id, false)}>Delete</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Box>

      <Box p={4} borderWidth="1px" borderRadius="lg" mt={4}>
        <Text fontWeight="bold" mb={3}>Released Vadla Links</Text>
        {releasedVadlaEntries.length === 0 ? (
          <Text color="gray.500" fontSize="sm">No released vadla entries.</Text>
        ) : (
          <Box overflowX="auto">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Vadla</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Item</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>No</th>
                  <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Principal</th>
                  <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Interest</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Released</th>
                  <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {releasedVadlaEntries.map((entry) => (
                  <tr key={entry._id}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{entry.vadlaUser?.name}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{entry.article?.nameOrNo || 'N/A'}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{entry.transferNo || '-'}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>₹{entry.principal}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>₹{entry.calculatedInterest ?? 0}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{formatDate(entry.releaseDate)}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                      <Button size="xs" variant="outline" mr={2} onClick={() => setViewEntry(entry)}>View</Button>
                      {canEdit && (
                        <Button size="xs" variant="outline" mr={2} onClick={() => openEditEntry(entry)}>Edit</Button>
                      )}
                      {canDelete && (
                        <Button size="xs" colorPalette="red" variant="outline" onClick={() => handleDeleteEntry(entry._id, true)}>Delete</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Box>

      <Dialog.Root open={releaseOpen} onOpenChange={(e) => !e.open && setReleaseOpen(false)} size="md">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Release Vadla Entry</Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              {releasingEntry && (
                <Stack gap={3}>
                  <Text fontSize="sm" color="gray.600">
                    Item: {releasingEntry.article?.nameOrNo || 'N/A'} · Vadla: {releasingEntry.vadlaUser?.name}
                  </Text>
                  <Box p={3} bg="gray.50" borderRadius="md">
                    <Text fontSize="sm"><b>Transfer date:</b> {formatDate(releasingEntry.transferDate)}</Text>
                    <Text fontSize="sm"><b>Principal:</b> ₹{releasingEntry.principal}</Text>
                    <Text fontSize="sm"><b>Interest rate:</b> {releasingEntry.interestRate}%</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>Release date</Text>
                    <Input type="date" value={releaseDate} onChange={(e) => handleReleaseDateChange(e.target.value)} />
                  </Box>
                  {releasePreview && (
                    <Box p={3} bg="green.50" borderRadius="md">
                      <Text fontSize="sm"><b>Interest till {formatDate(releasePreview.releaseDate)}:</b> ₹{releasePreview.interest}</Text>
                      <Text fontSize="md" fontWeight="bold" mt={1}>Total payable: ₹{releasePreview.totalPayable}</Text>
                    </Box>
                  )}
                </Stack>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={() => setReleaseOpen(false)}>Cancel</Button>
              <Button colorPalette="green" onClick={handleReleaseSubmit} loading={saving}>Confirm Release</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      <Dialog.Root open={editEntryOpen} onOpenChange={(e) => !e.open && setEditEntryOpen(false)} size="md">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Edit Vadla Entry</Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              {editingEntry && (
                <Stack gap={3}>
                  <Text fontSize="sm" color="gray.600">
                    Linked item: {editingEntry.article?.nameOrNo || 'N/A'} (reference only)
                  </Text>
                  <Box>
                    <Text fontSize="sm" mb={1}>No (optional)</Text>
                    <Input
                      value={editEntryForm.transferNo}
                      onChange={(e) => setEditEntryForm((p) => ({ ...p, transferNo: e.target.value }))}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>Principal</Text>
                    <Input
                      type="number"
                      value={editEntryForm.principal}
                      onChange={(e) => setEditEntryForm((p) => ({ ...p, principal: e.target.value }))}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>Interest Rate (%)</Text>
                    <Input
                      type="number"
                      value={editEntryForm.interestRate}
                      onChange={(e) => setEditEntryForm((p) => ({ ...p, interestRate: e.target.value }))}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>Transfer Date</Text>
                    <Input
                      type="date"
                      value={editEntryForm.date}
                      onChange={(e) => setEditEntryForm((p) => ({ ...p, date: e.target.value }))}
                    />
                  </Box>
                  {editingEntry.released && (
                    <Box>
                      <Text fontSize="sm" mb={1}>Release Date</Text>
                      <Input
                        type="date"
                        value={editEntryForm.releaseDate || ''}
                        onChange={(e) => setEditEntryForm((p) => ({ ...p, releaseDate: e.target.value }))}
                      />
                    </Box>
                  )}
                </Stack>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={() => setEditEntryOpen(false)}>Cancel</Button>
              <Button colorPalette="blue" onClick={handleEditEntrySubmit} loading={saving}>Save</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      <Dialog.Root open={!!viewEntry} onOpenChange={(e) => !e.open && setViewEntry(null)} size="md">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Vadla Entry</Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              {viewEntry && (
                <Stack gap={2} fontSize="sm">
                  <Text><b>Vadla:</b> {viewEntry.vadlaUser?.name || '-'}</Text>
                  <Text><b>Item:</b> {viewEntry.article?.nameOrNo || 'N/A'}</Text>
                  <Text><b>Client:</b> {viewEntry.article?.client?.name || '-'}</Text>
                  <Text><b>No:</b> {viewEntry.transferNo || '-'}</Text>
                  <Text><b>Principal:</b> ₹{viewEntry.principal}</Text>
                  <Text><b>Interest rate:</b> {viewEntry.interestRate}%</Text>
                  <Text><b>Transfer date:</b> {formatDate(viewEntry.transferDate)}</Text>
                  <Text><b>Status:</b> {viewEntry.released ? 'Released' : 'Active'}</Text>
                  {viewEntry.released && (
                    <>
                      <Text><b>Release date:</b> {formatDate(viewEntry.releaseDate)}</Text>
                      <Text><b>Interest:</b> ₹{viewEntry.calculatedInterest ?? 0}</Text>
                      <Text><b>Total payable:</b> ₹{viewEntry.totalPayable ?? 0}</Text>
                    </>
                  )}
                </Stack>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={() => setViewEntry(null)}>Close</Button>
              {canEdit && viewEntry && (
                <Button colorPalette="blue" onClick={() => { const e = viewEntry; setViewEntry(null); openEditEntry(e); }}>Edit</Button>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      <Dialog.Root open={!!splitDialogArticleId} onOpenChange={(e) => !e.open && closeSplitDialog()} size="sm">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Split item</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              {splitDialogArticleId && (() => {
                const article = articles.find((a) => a._id === splitDialogArticleId);
                const remaining = getArticleRemaining(article);
                const no = splitNoInput.trim();
                const preview = no ? buildSplitParts(no, remaining, splitAmountInput) : null;
                return (
                  <Stack gap={3}>
                    <Text fontSize="sm">Available: <b>₹{remaining}</b></Text>
                    <Box>
                      <Text fontSize="sm" mb={1}>No</Text>
                      <Input
                        value={splitNoInput}
                        onChange={(e) => setSplitNoInput(e.target.value)}
                        placeholder="e.g. 101"
                      />
                    </Box>
                    <Box>
                      <Text fontSize="sm" mb={1}>Split amount (₹)</Text>
                      <Input
                        type="number"
                        value={splitAmountInput}
                        onChange={(e) => setSplitAmountInput(e.target.value)}
                        placeholder={`Less than ${remaining}`}
                      />
                    </Box>
                    {preview && (
                      <Box p={2} bg="gray.50" borderRadius="md" fontSize="sm">
                        <Text>{no}/1 — ₹{preview[0].principal}</Text>
                        <Text>{no}/2 — ₹{preview[1].principal}</Text>
                      </Box>
                    )}
                  </Stack>
                );
              })()}
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={closeSplitDialog}>Cancel</Button>
              <Button colorPalette="teal" onClick={applySplit}>Create split</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      <Dialog.Root open={transferOpen} onOpenChange={(e) => !e.open && setTransferOpen(false)} size="xl">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="85vh" overflowY="auto">
            <Dialog.Header>
              <Dialog.Title>Transfer — enter details for each item</Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              <Stack gap={4}>
                {selectedIds.map((articleId) => {
                  const article = articles.find((a) => a._id === articleId);
                  const row = transferForms[articleId] || emptyTransferRow();
                  return (
                    <Box key={articleId} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                      <Text fontWeight="bold" fontSize="sm" mb={2}>
                        {article?.nameOrNo || 'Item'} — {article?.client?.name || article?.clientId?.name}
                      </Text>
                      <Stack gap={2}>
                        <Text fontSize="xs" color="gray.600">
                          Available to transfer: ₹{getArticleRemaining(article)}
                        </Text>
                        {!row.splitParts ? (
                          <>
                            <Box>
                              <Text fontSize="xs" mb={1}>No (optional)</Text>
                              <Input
                                value={row.transferNo}
                                onChange={(e) => updateTransferForm(articleId, 'transferNo', e.target.value)}
                              />
                            </Box>
                            <Flex gap={2} wrap="wrap" align="flex-end">
                              <Box flex={1} minW="120px">
                                <Text fontSize="xs" mb={1}>Principal</Text>
                                <Input
                                  type="number"
                                  value={row.principal}
                                  onChange={(e) => updateTransferForm(articleId, 'principal', e.target.value)}
                                />
                              </Box>
                              <Button size="sm" variant="outline" onClick={() => openSplitDialog(articleId)}>
                                Split
                              </Button>
                            </Flex>
                          </>
                        ) : (
                          <Box p={2} bg="blue.50" borderRadius="md">
                            <Flex justify="space-between" align="center" mb={2}>
                              <Text fontSize="sm" fontWeight="bold">Split parts — select for vadla</Text>
                              <Button size="xs" variant="ghost" onClick={() => clearSplit(articleId)}>
                                Clear split
                              </Button>
                            </Flex>
                            {row.splitParts.map((part) => (
                              <Flex key={part.id} align="center" gap={2} mb={1}>
                                <Checkbox.Root
                                  checked={part.selected}
                                  onCheckedChange={() => toggleSplitPart(articleId, part.id)}
                                >
                                  <Checkbox.HiddenInput />
                                  <Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
                                </Checkbox.Root>
                                <Text fontSize="sm">
                                  <b>{part.transferNo}</b> — ₹{part.principal}
                                </Text>
                              </Flex>
                            ))}
                          </Box>
                        )}
                        <Flex gap={2} wrap="wrap">
                          <Box flex={1} minW="120px">
                            <Text fontSize="xs" mb={1}>Interest Rate (%)</Text>
                            <Input
                              type="number"
                              value={row.interestRate}
                              onChange={(e) => updateTransferForm(articleId, 'interestRate', e.target.value)}
                            />
                          </Box>
                          <Box flex={1} minW="140px">
                            <Text fontSize="xs" mb={1}>Date</Text>
                            <Input
                              type="date"
                              value={row.date}
                              onChange={(e) => updateTransferForm(articleId, 'date', e.target.value)}
                            />
                          </Box>
                        </Flex>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={() => setTransferOpen(false)}>Cancel</Button>
              <Button colorPalette="teal" onClick={handleTransferSubmit} loading={saving}>
                Confirm Transfer
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
};

const Vadla = () => {
  return (
    <Box>
      <Heading size="lg" mb={6}>Vadla</Heading>
      <Tabs.Root defaultValue="users">
        <Tabs.List>
          <Tabs.Trigger value="users">Create Vadla User</Tabs.Trigger>
          <Tabs.Trigger value="entry">Vadla Entry</Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value="users" pt={4}>
          <VadlaUsersTab />
        </Tabs.Content>
        <Tabs.Content value="entry" pt={4}>
          <VadlaEntryTab />
        </Tabs.Content>
      </Tabs.Root>
    </Box>
  );
};

export default Vadla;
